import { APIClient, ApiQueryParams } from '@frontend/api-utils';
import { Token } from '@frontend/authentication-v2';
import {
    CreateIdentification,
    Identification,
    IdentificationListResponse,
    IdentificationQueryParams,
    LookupRequest,
    LookupResponse,
    UpdateIdentification
} from '@frontend/identification/types';

export class IdentificationClient extends APIClient {
    public static readonly endpoint = '/identification-api/v1';

    public static async fetchIdentifications(queryParams?: ApiQueryParams<IdentificationQueryParams>): Promise<IdentificationListResponse> {
        return await this.apiPaginated<IdentificationListResponse, IdentificationQueryParams>(`${this.endpoint}/identifications`, queryParams);
    }

    public static async resolveIdentifications(ids: string[]): Promise<IdentificationListResponse> {
        const response = await this.post(`${this.endpoint}/identifications-resolve`, { ids: ids });
        return await this.handleResponse<IdentificationListResponse>(response);
    }

    public static async deleteIdentifications(ids: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/identifications-delete`, { ids: ids });
        return await this.handleVoidResponse(response);
    }

    public static async postIdentification(
        accountId: string,
        body: CreateIdentification,
        includeAccessToken: boolean = true,
        token?: Token
    ): Promise<Identification> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/identifications`, body, includeAccessToken, token);
        return await this.handleResponse<Identification>(response);
    }

    public static async fetchIdentification(accountId: string, identificationId: string): Promise<Identification> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/identifications/${identificationId}`);
        return await this.handleResponse<Identification>(response);
    }

    public static async patchIdentification(
        accountId: string,
        identificationId: string,
        body: UpdateIdentification,
        includeAccessToken: boolean = true,
        token?: Token
    ): Promise<Identification> {
        const response = await this.patch(`${this.endpoint}/accounts/${accountId}/identifications/${identificationId}`, body, includeAccessToken, token);
        return await this.handleResponse<Identification>(response);
    }

    public static async deleteIdentification(accountId: string, identificationId: string): Promise<void> {
        const response = await this.delete(`${this.endpoint}/accounts/${accountId}/identifications/${identificationId}`);
        return await this.handleVoidResponse(response);
    }

    public static async lookup(body: LookupRequest): Promise<LookupResponse> {
        const response = await this.post(`${this.endpoint}/lookup`, body);
        return this.handleResponse<LookupResponse>(response);
    }

    public static async postIoTSeed(accountId: string, iotId: string, body: { account_id: string }): Promise<void> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/iots/${iotId}/seed`, body);
        return this.handleResponse(response);
    }
}
